import { useEffect, useRef, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { GlobalWebSocketManager } from '@/lib/GlobalWebSocketManager'
import { queryKeys } from '../keys'
import type { Message } from '@/types/conversations'
import type { Conversation } from '@/services/conversationsApi'

interface MessageData {
  id?: string
  text?: string
  message?: string
  content?: string
  sender?: string
  timestamp?: string
  conversation_id?: string
  message_count?: number
  user_display_name?: string
  source?: string
  platform?: string
  user?: string
}

interface ConversationUpdateData {
  id?: string
  conversation_id?: string
  status?: string
  ui_status?: string
  old_status?: string
  new_status?: string
  updated_at?: string
  timestamp?: string
  title?: string
  conversation_title?: string
  description?: string
  metadata?: Record<string, unknown>
}

/**
 * Hook to sync WebSocket events with TanStack Query cache
 * Automatically invalidates and updates queries when WebSocket events arrive
 */
export function useWebSocketSync() {
  const queryClient = useQueryClient()
  const wsManager = GlobalWebSocketManager.getInstance()
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  /**
   * Handle new message events
   * Updates conversation messages cache and invalidates conversation list
   */
  const handleNewMessage = useCallback((conversationId: string, messageData: MessageData) => {
    // Optimistically add message to messages query cache
    queryClient.setQueriesData<{ pages: Array<{ results?: Message[] }> }>(
      { queryKey: queryKeys.conversations.messages(conversationId) },
      (old) => {
        if (!old) return old

        // Check if message already exists to avoid duplicates
        const messageText = messageData.text || messageData.message || messageData.content || ''
        const messageExists = old.pages.some(page =>
          page.results?.some(msg =>
            msg.text === messageText &&
            msg.sender === messageData.sender &&
            Math.abs(
              new Date(msg.timestamp).getTime() -
              new Date(messageData.timestamp || new Date()).getTime()
            ) < 10000 // 10 second window
          )
        )

        if (messageExists) {
          return old
        }

        // Add new message to the first page
        const newMessage: Message = {
          id: messageData.id || Date.now().toString(),
          text: messageText,
          sender: (messageData.sender as 'user' | 'assistant' | 'admin') || 'user',
          timestamp: messageData.timestamp || new Date().toISOString(),
          conversation: conversationId,
        }

        const updatedPages = [...old.pages]
        if (updatedPages[0]?.results) {
          updatedPages[0] = {
            ...updatedPages[0],
            results: [newMessage, ...(updatedPages[0].results || [])],
          }
        } else {
          updatedPages[0] = { results: [newMessage] }
        }

        return { ...old, pages: updatedPages }
      }
    )

    // Invalidate conversation list to update last message preview
    queryClient.invalidateQueries({ 
      queryKey: queryKeys.conversations.lists(),
      refetchType: 'active', // Only refetch active queries
    })

    // Invalidate conversation detail to update metadata
    queryClient.invalidateQueries({
      queryKey: queryKeys.conversations.detail(conversationId),
      refetchType: 'active',
    })
  }, [queryClient])

  /**
   * Handle conversation update events
   * Invalidates conversation queries to refetch updated data
   */
  const handleConversationUpdate = useCallback((conversationId: string, updateData: ConversationUpdateData) => {
    // Optimistically update conversation in cache if we have the data
    if (updateData.status || updateData.title || updateData.description) {
      queryClient.setQueriesData<Conversation>(
        { queryKey: queryKeys.conversations.detail(conversationId) },
        (old) => {
          if (!old) return old
          return {
            ...old,
            status: (updateData.status || updateData.new_status || old.status) as Conversation['status'],
            title: updateData.title || updateData.conversation_title || old.title,
            description: updateData.description || old.description,
            timestamp: updateData.updated_at || updateData.timestamp || old.timestamp,
          }
        }
      )

      // Update conversation in list cache
      queryClient.setQueriesData<{ pages: Array<{ results?: Conversation[] }> }>(
        { queryKey: queryKeys.conversations.lists() },
        (old) => {
          if (!old) return old
          return {
            ...old,
            pages: old.pages.map(page => ({
              ...page,
              results: page.results?.map(conv =>
                conv.id === conversationId
                  ? {
                      ...conv,
                      status: (updateData.status || updateData.new_status || conv.status) as Conversation['status'],
                      title: updateData.title || updateData.conversation_title || conv.title,
                      description: updateData.description || conv.description,
                      timestamp: updateData.updated_at || updateData.timestamp || conv.timestamp,
                    }
                  : conv
              ),
            })),
          }
        }
      )
    }

    // Invalidate to ensure we have the latest data
    queryClient.invalidateQueries({
      queryKey: queryKeys.conversations.detail(conversationId),
      refetchType: 'active',
    })

    queryClient.invalidateQueries({
      queryKey: queryKeys.conversations.lists(),
      refetchType: 'active',
    })
  }, [queryClient])

  useEffect(() => {
    // Subscribe to WebSocket events
    const unsubscribe = wsManager.subscribe((conversationId, messageData, eventType) => {
      if (!conversationId) return

      // Debounce rapid events to avoid excessive cache updates
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }

      debounceTimeoutRef.current = setTimeout(() => {
        if (eventType === 'new_message') {
          handleNewMessage(conversationId, messageData as MessageData)
        } else if (eventType === 'conversation_update') {
          handleConversationUpdate(conversationId, messageData as ConversationUpdateData)
        }
      }, 300) // 300ms debounce
    })

    return () => {
      unsubscribe()
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [handleConversationUpdate, handleNewMessage, wsManager])

  return {
    isConnected: wsManager.isConnected(),
    connectionState: wsManager.getConnectionState(),
  }
}
